import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Paper } from '@mui/material'
import {
  DataGridPremium,
  GridColDef,
  useGridApiRef,
  gridFilterModelSelector,
  gridFilteredTopLevelRowCountSelector,
  GridPaginationModel,
  GridPinnedColumns,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowHeightParams,
} from '@mui/x-data-grid-premium'
import { TableToolbar } from 'src/components/ui'
import { AdminAlert } from 'src/api'
import { SearchParamsKeys } from 'src/enums'
import { defaultRowsValuePerPage } from 'src/config'
import { AlertColumns } from './alertColumns'

const defaultRowsPerPageOptions = defaultRowsValuePerPage

interface AlertListProps {
  alerts: AdminAlert[]
  alertCount: number
  isLoadingAlerts: boolean
  queryParams: Record<string, string>
  setQueryParams: (params: Record<string, string>, paramsToRemove?: string[]) => void
  autoHeight?: boolean
  height?: string
  alertColumns?: GridColDef<AdminAlert>[]
  exportedFileName?: string
}

export const AlertList: FC<AlertListProps> = ({
  alertCount,
  alerts,
  isLoadingAlerts,
  setQueryParams,
  queryParams,
  autoHeight = false,
  height = '100%',
  alertColumns,
  exportedFileName,
}) => {
  const apiRef = useGridApiRef()
  const defaultColumns = useMemo((): GridColDef<AdminAlert>[] => ([
    AlertColumns.Name,
    AlertColumns.Status,
    AlertColumns.Type,
    AlertColumns.Contractor,
    AlertColumns.Project,
    AlertColumns.Section,
    AlertColumns.Sensor,
    AlertColumns.RealtedProducts,
    AlertColumns.StartedAt,
    AlertColumns.EndedAt,
    AlertColumns.Duration,
  ]), [])
  const columns = alertColumns?.length ? alertColumns : defaultColumns
  const [rowCountState, setRowCountState] = useState(alertCount)
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, ""],
  })

  useEffect(() => {
    setRowCountState((prevRowCountState: number) =>
      alertCount !== undefined ? alertCount : prevRowCountState
    )
  }, [alertCount, setRowCountState])

  const handlePinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumns) => {
      setPinnedColumns(updatedPinnedColumns)
    },
    []
  )

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    const { page, pageSize } = pagination
    const newPage = page + 1
    if (setQueryParams) {
      setQueryParams({
        [SearchParamsKeys.Page]: `${newPage}`,
        [SearchParamsKeys.RowsPerPage]: `${pageSize}`,
      }, [
        SearchParamsKeys.Page,
        SearchParamsKeys.RowsPerPage,
      ])
    }
  }

  const handleChangeState = async () => {
    const filterModel = gridFilterModelSelector(apiRef.current.state)
    if (filterModel.items.length > 0 && filterModel.items[0].value) {
      const visibleRowCount = gridFilteredTopLevelRowCountSelector(apiRef.current.state)
      if (visibleRowCount) setRowCountState(visibleRowCount)
    } else {
      setRowCountState(alertCount)
    }
  }

  const page = queryParams["page"] ? parseInt(queryParams["page"], 10) - 1 : 0
  const pageSize = parseInt(queryParams["rowsPerPage"], 10) || defaultRowsPerPageOptions[0]

  const getRowHeight = useCallback(({ id, densityFactor }: GridRowHeightParams) => {
    const alert = alerts.find((item) => item.id === id)
    if (alert && alert.alertsProductsProjectSections?.length === 0) {
      return 45 * densityFactor
    }
    return 'auto'
  }, [alerts])

  return (
    <Paper sx={{ flexGrow: 1, minHeight: '200px', height, width: "100%" }}>
      <DataGridPremium<AdminAlert>
        apiRef={apiRef}
        pagination
        autoHeight={autoHeight}
        pinnedColumns={pinnedColumns}
        disableRowSelectionOnClick
        rows={alerts}
        columns={columns}
        rowCount={rowCountState}
        loading={isLoadingAlerts}
        paginationModel={{ page, pageSize }}
        getRowHeight={getRowHeight}
        rowThreshold={2}
        columnThreshold={2}
        paginationMode="server"
        pageSizeOptions={defaultRowsPerPageOptions}
        onPaginationModelChange={handleChangePaginationModel}
        onStateChange={handleChangeState}
        localeText={{
          columnMenuSortAsc: "Sort A-Z",
          columnMenuSortDesc: "Sort Z-A",
        }}
        slots={{
          toolbar: () => <TableToolbar exportedFileName={exportedFileName} />
        }}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        sx={(theme) => ({
          border: "none",
          "& .MuiDataGrid-columnHeaderTitleContainerContent": {
            paddingLeft: "5px",
            overflow: "visible",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: 1,
            fontSize: "13px",
            fontWeight: 400,
            color: "rgba(0, 0, 0, 0.6)",
            overflow: "visible",
          },
        })}
      />
    </Paper>
  )
}
